<template>
    <div>
        <input type="hidden" :name="fieldData.code" :value="this.selectedOption">

        <label v-if="!tableView" class="form-label d-flex align-items-center flex-wrap">
            {{ fieldData['title_' + getLang()] }}
            <span class="input-group__required" v-if="fieldData.required">*</span>

            <info-label
                v-if="fieldData.settings && fieldData.settings.tooltip"
                :text="fieldData.settings.tooltip"
            />
        </label>

        <div
            :key="entries.id"
            v-for="(entries, index) in entriesList"
            :class="tableView ? '' : 'padding-bottom-12'"
        >
            <select
                @change="onEntryChange(index)"
                v-model="dictionaryResult[index]"
                ref="select"
                :class="tableView ? 'table-input' : 'form-control-1'"
                :disabled="!isEditible">
                <option :key="entry.id" v-for="entry in entries" :value="JSON.stringify({'id': entry.id, 'key': entry.key})">
                    {{ entry.value }}
                </option>
            </select>
        </div>
        <div v-if="!tableView && validationErrors">
            <span v-for="error in validationErrors" class="error-text d-block">{{ error }}</span>
        </div>
    </div>
</template>

<script>
import axios from 'axios';
import $ from 'jquery';
import 'select2';
import InfoLabel from '../ui/InfoLabel.vue';

export default {
    name: 'dictionary',
    components: {InfoLabel},
    props: {
        field: Object,
        isEditible: Boolean,
        oldData: String,
        tableView: Boolean,
        modelValue: String,
        validationErrors: Object,
    },
    data() {
        return {
            fieldData: {},
            dictionaryResult: {},
            entriesList: {},
            isManualChange: false,
        };
    },
    computed: {
        selectedOption: {
            get() {
                return this.modelValue;
            },
            set(value) {
                this.$emit('update:modelValue', value);
            },
        },
    },
    watch: {
        // Наблюдаем за изменениями entriesList и инициализируем select2
        entriesList: {
            deep: true,
            handler() {
                this.$nextTick(() => {
                    this.initializeSelect2();
                });
            },
        },
    },
    mounted() {
        this.fieldData = this.field;

        let settings = this.fieldData.settings;

        if (Array.isArray(settings)) {
            settings = settings.length > 0 ? settings[0] : {};
        }

        this.fieldData.settings = settings || {};

        this.selectedOption = this.oldData || '';
        this.initializeEntries();
    },
    methods: {
        getLang() {
            return document.querySelector('html').getAttribute('lang') || 'ru';
        },
        async getEntries() {
            try {
                const response = await axios.get(`/api/v1/forms/dictionary-entries/${this.fieldData.dictionary_id}`);
                this.entriesList[0] = response.data;
            } catch (error) {
                console.error('Error fetching entries:', error);
            }
        },

        async initializeEntries() {
            await this.getEntries(); // Получаем первый уровень

            if (this.selectedOption !== '') {
                this.autoFillDictionaryResult(0, this.selectedOption);
                await this.fetchAllLevelsByValue();

                // Если после восстановления всё ещё только 1‑й уровень, но он выбран —
                // принудительно загрузим следующий уровень
                const hasLevel0Selection = !!this.dictionaryResult[0];
                const levelsCount = this.getObjectLength(this.entriesList);
                if (levelsCount === 1 && hasLevel0Selection) {
                    await this.onEntryChange(0);
                }
                return;
            }

            await this.applyDefaultIfEmpty();
        },
        initializeSelect2() {
            this.$refs.select.forEach((selectEl, index) => {
                if (!$(selectEl).data('select2')) { // Чтобы избежать повторной инициализации select2
                    $(selectEl).select2({
                        width: '100%',
                    }).on('change', (event) => {
                        if (!this.isManualChange) {
                            this.dictionaryResult[index] = $(event.target).val(); // Передаём значение оригиналу
                            this.onEntryChange(index); // вызываем событие изменения значения
                        }
                    });
                }
                // Применяем классы вручную, так как selectionCssClass не поддерживается в select2 версии 4.0.13
                $(selectEl).next('.select2-container').find('.select2-selection').addClass(
                    this.tableView ? 'table-input' : (this.validationErrors ? 'form-control-1 select2-form-control border-danger' : 'form-control-1 select2-form-control')
                );

                // Принудительно устанавливаем значение, если оно уже есть
                const selectedValue = this.dictionaryResult[index];
                if (selectedValue) {
                    this.isManualChange = true;
                    $(selectEl).val(selectedValue).trigger('change');
                    this.isManualChange = false;
                }
            });
        },
        autoFillDictionaryResult(index, value) {
            // Пытаемся найти в текущем уровне совпадение и записываем в результаты
            const matchedEntry = this.findEntryByValue(this.entriesList[index], value);
            if (matchedEntry) {
                this.dictionaryResult[index] = JSON.stringify({id: matchedEntry.id, key: matchedEntry.key});
            }
        },
        findEntryByValue(entries, value) {
            return entries.find(entry => entry.key === value);
        },
        findEntryById(entries, id) {
            return entries.find(entry => String(entry.id) === String(id));
        },
        getDefaultEntryId() {
            return this.fieldData && this.fieldData.dictionary.default_value ? this.fieldData.dictionary.default_value : null;
        },
        async applyDefaultIfEmpty() {
            try {
                const defaultId = this.getDefaultEntryId();
                if (!defaultId) return;

                const level0 = this.entriesList[0] || [];
                const matched = this.findEntryById(level0, defaultId);
                if (!matched) return;

                this.dictionaryResult[0] = JSON.stringify({id: matched.id, key: matched.key});
                this.selectedOption = matched.key;

                await this.onEntryChange(0);
            } catch (e) {
                console.error('Error applying default value:', e);
            }
        },
        hasDictionaryResult() {
            return this.getObjectLength(this.dictionaryResult) > 0;
        },
        async fetchAllLevelsByValue() {
            try {
                // Получаем все уровни по сохраненному значению
                const response = await this.apiGetAllLevelsByValue(this.fieldData.dictionary_id, this.selectedOption);
                if (response.length > 0) {
                    this.populateLevels(response);
                }
            } catch (error) {
                console.error('Error fetching levels:', error);
            }
        },
        async apiGetAllLevelsByValue(dictionaryId, value) {
            const response = await axios.get(`/api/v1/dictionary-entries/levels/${dictionaryId}/${value}`);
            return response.data;
        },
        populateLevels(data) {
            // Выбираем из всей цепочки значения и их списки
            Object.values(data).forEach(level => {
                if (level.parentKey && level.value) {
                    this.autoFillDictionaryResult(this.getObjectLength(this.entriesList) - 1, level.parentKey);
                    this.entriesList[this.getObjectLength(this.entriesList)] = level.value;
                }
            });

            this.autoFillDictionaryResult(this.getObjectLength(this.entriesList) - 1, this.selectedOption);
        },
        async onEntryChange(index) {
            const parentId = JSON.parse(this.dictionaryResult[index]).id;

            // При обновлении значения по индексу, сносим все результаты после него
            this.dictionaryResult = this.removeEntriesAfterIndex(this.dictionaryResult, index);
            this.clearSelect2AfterIndex(index);
            this.entriesList = this.removeEntriesAfterIndex(this.entriesList, index);
            // Обновляем последнее значение
            this.selectedOption = this.getLastObjectValue(this.dictionaryResult)?.key;
            try {
                // получаем следующий уровень при наличии
                const response = await this.apiGetEntriesByParent(parentId);
                if (response.length > 0) {
                    this.entriesList[Number(index) + 1] = response;
                }
            } catch (error) {
                console.error('Error fetching entries by parent:', error);
            }
        },
        async apiGetEntriesByParent(parentId) {
            const response = await axios.get(`/api/v1/dictionary-entries/${parentId}`);
            return response.data;
        },
        getObjectLength(object) {
            return Object.keys(object).length;
        },
        getLastObjectValue(object) {
            return JSON.parse(object[this.getObjectLength(object) - 1]);
        },
        removeEntriesAfterIndex(object, indexToStop) {
            return Object.keys(object).reduce((acc, key) => {
                if (Number(key) <= indexToStop) acc[key] = object[key];
                return acc;
            }, {});
        },
        clearSelect2(index) {
            $(this.$refs.select[index]).select2('destroy');
        },
        clearSelect2AfterIndex(indexToStop) {
            Object.keys(this.entriesList).forEach((key) => {
                if (Number(key) > indexToStop && this.$refs.select[key]) {
                    this.clearSelect2(key);
                }
            });
        },
    },
};
</script>
